import React, { useState } from 'react';
import { getAssetSitePath } from '../lib/utils';

export default function MediaEmbed({ videoUrl, videoFile, poster, title }) {
  const [isPlaying, setIsPlaying] = useState(false);

  // Check if we have an uploaded video file or a video URL
  const src = (Array.isArray(videoFile) ? videoFile[0] : videoFile) || videoUrl;

  if (!src) return null;

  // 1. YouTube detection
  const youtubeMatch = src.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  if (youtubeMatch) {
    const videoId = youtubeMatch[1];
    const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
    const posterUrl = poster || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

    if (!isPlaying) {
      return (
        <div
          className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-md group cursor-pointer"
          onClick={() => setIsPlaying(true)}
        >
          <img
            src={posterUrl}
            alt={title || 'Video miniature'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 filter brightness-90"
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-cta text-clay-950 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
              <i className="fa-solid fa-play text-xl sm:text-2xl ml-1" aria-hidden="true" />
            </div>
          </div>
          <span className="sr-only">Afspil video</span>
        </div>
      );
    }

    return (
      <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-md">
        <iframe
          src={embedUrl}
          title={title || 'YouTube video'}
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // 2. Vimeo detection
  const vimeoMatch = src.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch) {
    const vimeoId = vimeoMatch[1];
    const embedUrl = `https://player.vimeo.com/video/${vimeoId}?dnt=1`;

    return (
      <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-md">
        <iframe
          src={embedUrl}
          title={title || 'Vimeo video'}
          className="w-full h-full border-0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // 3. Direct video file / MP4
  const videoSrc = getAssetSitePath(src);
  const isVertical = src.toLowerCase().includes('die-on-this-hill') || src.toLowerCase().includes('oever') || src.toLowerCase().includes('vertical') || src.toLowerCase().includes('9-16');

  if (isVertical) {
    return (
      <div className="relative w-full max-w-[320px] sm:max-w-sm mx-auto aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-md flex items-center justify-center">
        <video
          controls
          playsInline
          preload="metadata"
          poster={poster}
          className="w-full h-full object-contain"
        >
          <source src={videoSrc} type="video/mp4" />
          Din browser understøtter desværre ikke HTML5 video.
        </video>
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-md">
      <video
        controls
        playsInline
        preload="auto"
        poster={poster}
        className="w-full h-full object-cover"
      >
        <source src={videoSrc} type="video/mp4" />
        Din browser understøtter desværre ikke HTML5 video.
      </video>
    </div>
  );
}
